import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from '../../utils/axios';
import { useTranslation } from 'react-i18next';
import { 
  Loader2, 
  Search, 
  MapPin, 
  Phone, 
  Globe, 
  Image as ImageIcon,
  Palette,
  Check,
  ArrowRight,
  ArrowLeft,
  X,
  Building2,
  Upload,
  Cloud,
  CheckCircle
} from 'lucide-react';
import { toast } from 'react-hot-toast';

interface GooglePlace {
  placeId: string;
  name: string;
  address: string;
  logoUrl?: string;
}

interface RestaurantDetails {
  name: string;
  address: string;
  phone?: string;
  website?: string;
  logoUrl?: string;
  colors?: {
    primary?: string;
    secondary?: string;
    accent?: string;
  };
  socialMedia?: {
    facebook?: string;
    instagram?: string;
    twitter?: string;
  };
}

interface CloudinaryUploadResponse {
  success: boolean;
  data: {
    menuId: string;
    restaurantId: string;
    logoUrl: string;
    backgroundImageUrl: string;
    backgroundType: string;
    styleConfig: any;
  };
  message: string;
}

const CreateMenuWizard = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState<string>('');
  
  // Step 1: Restaurant Search
  const [restaurantName, setRestaurantName] = useState('');
  const [searchResults, setSearchResults] = useState<GooglePlace[]>([]);
  const [selectedPlace, setSelectedPlace] = useState<GooglePlace | null>(null);
  const [isManualEntry, setIsManualEntry] = useState(false);
  
  // Step 2: Restaurant Details
  const [restaurantDetails, setRestaurantDetails] = useState<RestaurantDetails | null>(null);
  
  // Step 3: Theme & Style
  const [theme, setTheme] = useState<'light' | 'dark'>('light');
  const [backgroundColor, setBackgroundColor] = useState('#ffffff');
  const [primaryColor, setPrimaryColor] = useState('#3B82F6');
  
  // Step 4: Background Selection
  const [backgroundType, setBackgroundType] = useState<'color' | 'image'>('color');
  const [backgroundImageFile, setBackgroundImageFile] = useState<File | null>(null);
  const [backgroundImagePreview, setBackgroundImagePreview] = useState<string>('');
  
  // Step 5: Logo Upload
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string>('');
  const [useExistingLogo, setUseExistingLogo] = useState(true);

  useEffect(() => {
    if (selectedPlace) {
      fetchRestaurantDetails();
    }
  }, [selectedPlace]);

  useEffect(() => {
    if (restaurantDetails?.logoUrl) {
      setLogoPreview(restaurantDetails.logoUrl);
      setUseExistingLogo(true);
    }
  }, [restaurantDetails]);

  const searchRestaurant = async () => {
    if (!restaurantName.trim()) {
      toast.error(t('create_menu_wizard.messages.enter_restaurant_name'));
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post('/menu/search', 
        { name: restaurantName },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.bestMatch || response.data.alternatives?.length > 0) {
        const results = [
          ...(response.data.bestMatch ? [response.data.bestMatch] : []),
          ...(response.data.alternatives || [])
        ];
        setSearchResults(results);
      } else {
        toast.error(t('create_menu_wizard.messages.no_restaurants_found'));
        setSearchResults([]);
      }
    } catch (error: any) {
      console.error('Search error:', error);
      toast.error(error.response?.data?.error || t('create_menu_wizard.messages.search_failed'));
    } finally {
      setLoading(false);
    }
  };

  const fetchRestaurantDetails = async () => {
    if (!selectedPlace) return;

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post('/menu/details',
        { placeId: selectedPlace.placeId },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.menuData) {
        const details = response.data.menuData;
        setRestaurantDetails(details);
        
        // Auto-set colors if available
        if (details.colors?.primary) {
          setPrimaryColor(details.colors.primary);
        }
      }
    } catch (error: any) {
      console.error('Details error:', error);
      toast.error(error.response?.data?.error || t('create_menu_wizard.messages.details_fetch_failed'));
    } finally {
      setLoading(false);
    }
  };

  const handlePlaceSelect = (place: GooglePlace) => {
    setSelectedPlace(place);
    setIsManualEntry(false);
    setStep(2);
  };

  const handleAddManually = () => {
    setSelectedPlace(null);
    setIsManualEntry(true);
    setRestaurantDetails({
      name: restaurantName.trim() || '',
      address: '',
      phone: '',
      website: '',
      logoUrl: undefined,
      colors: undefined,
      socialMedia: undefined,
    });
    setStep(2);
  };

  const handleCreateMenu = async () => {
    if (!restaurantDetails) {
      toast.error(t('create_menu_wizard.messages.complete_all_steps'));
      return;
    }
    if (!restaurantDetails.name?.trim()) {
      toast.error(t('create_menu_wizard.messages.enter_restaurant_name'));
      return;
    }

    setLoading(true);
    setUploadStatus(t('create_menu_wizard.messages.upload_preparing'));
    
    try {
      const token = localStorage.getItem('token');
      
      // Create FormData to send both JSON data and files
      const formData = new FormData();
      
      // Add JSON data
      if (isManualEntry || !selectedPlace) {
        formData.append('manualEntry', 'true');
        formData.append('placeId', '');
      } else {
        formData.append('placeId', selectedPlace!.placeId);
      }
      formData.append('theme', theme);
      formData.append('primaryColor', primaryColor);
      formData.append('restaurantName', restaurantDetails.name.trim());
      formData.append('restaurantAddress', restaurantDetails.address?.trim() || '');
      if (restaurantDetails.phone) formData.append('restaurantPhone', restaurantDetails.phone);
      if (restaurantDetails.website) formData.append('restaurantWebsite', restaurantDetails.website || '');
      formData.append('backgroundType', backgroundType);
      
      // Handle background image
      if (backgroundType === 'image' && backgroundImageFile) {
        setUploadStatus(t('create_menu_wizard.messages.upload_background'));
        formData.append('backgroundImage', backgroundImageFile);
      } else if (backgroundType === 'color') {
        formData.append('background', backgroundColor);
      }
      
      // Handle logo
      if (!useExistingLogo && logoFile) {
        setUploadStatus(t('create_menu_wizard.messages.upload_logo'));
        formData.append('logo', logoFile);
      } else if (useExistingLogo && restaurantDetails.logoUrl) {
        formData.append('logoUrl', restaurantDetails.logoUrl);
      }
      
      // Prepare style config
      const styleConfig: any = {
        theme,
        primary: primaryColor,
        backgroundType
      };
      
      if (backgroundType === 'color') {
        styleConfig.background = backgroundColor;
      } else if (backgroundType === 'image') {
        styleConfig.background = null;
      }
      
      // Add colors from restaurant if available
      if (restaurantDetails.colors) {
        Object.assign(styleConfig, restaurantDetails.colors);
      }
      
      formData.append('styleConfig', JSON.stringify(styleConfig));
      
      console.log('Creating menu with:', {
        placeId: selectedPlace?.placeId ?? '(manual)',
        hasBackgroundImage: backgroundType === 'image' && !!backgroundImageFile,
        hasLogo: !useExistingLogo && !!logoFile,
        useExistingLogo: useExistingLogo,
        styleConfig
      });
      
      setUploadStatus(t('create_menu_wizard.messages.creating_menu'));
      
      // Create the menu
      const response = await axios.post<CloudinaryUploadResponse>('/menu/create-from-google',
        formData,
        { 
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          } 
        }
      );

      if (response.data.success) {
        setUploadStatus('✅ Menu creato con successo!');
        toast.success(t('create_menu_wizard.messages.menu_created'));
        
        // Show Cloudinary URLs in console for debugging
        console.log('Cloudinary URLs:', {
          logoUrl: response.data.data.logoUrl,
          backgroundImageUrl: response.data.data.backgroundImageUrl,
          backgroundType: response.data.data.backgroundType
        });
        
        // Wait a moment to show success message
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        navigate(`/menus/${response.data.data.menuId}`);
      } else {
        toast.error(t('create_menu_wizard.messages.creation_failed'));
      }
    } catch (error: any) {
      console.error('Create error:', error);
      setUploadStatus('❌ Caricamento fallito');
      toast.error(error.response?.data?.error || t('create_menu_wizard.messages.creation_failed'));
      
      if (process.env.NODE_ENV === 'development') {
        console.error('Error details:', error.response?.data?.details);
      }
    } finally {
      setLoading(false);
    }
  };

  const steps = [
    { number: 1, title: t('create_menu_wizard.steps.restaurant_search'), icon: Search },
    { number: 2, title: t('create_menu_wizard.steps.restaurant_details'), icon: Building2 },
    { number: 3, title: t('create_menu_wizard.steps.theme_style'), icon: Palette },
    { number: 4, title: t('create_menu_wizard.steps.background'), icon: ImageIcon },
    { number: 5, title: t('create_menu_wizard.steps.logo_upload'), icon: ImageIcon }
  ];

  return (
    <div className="max-w-4xl mx-auto p-6">
      {/* Progress Steps */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          {steps.map((s, idx) => (
            <React.Fragment key={s.number}>
              <div className="flex flex-col items-center flex-1">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center border-2 transition-colors ${
                  step >= s.number 
                    ? 'bg-[#0A0C0B] border-[#0A0C0B] text-white' 
                    : 'bg-white border-gray-300 text-gray-400'
                }`}>
                  {step > s.number ? (
                    <Check className="w-6 h-6" />
                  ) : (
                    <s.icon className="w-6 h-6" />
                  )}
                </div>
                <span className={`mt-2 text-sm font-medium ${
                  step >= s.number ? 'text-[#0A0C0B]' : 'text-gray-400'
                }`}>
                  {s.title}
                </span>
              </div>
              {idx < steps.length - 1 && (
                <div className={`flex-1 h-0.5 mx-4 ${
                  step > s.number ? 'bg-[#0A0C0B]' : 'bg-gray-300'
                }`} />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Upload Status Indicator */}
      {loading && uploadStatus && (
        <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-center gap-3">
            <Loader2 className="w-5 h-5 text-blue-600 animate-spin" />
            <div className="flex-1">
              <p className="text-sm font-medium text-blue-800">{uploadStatus}</p>
              <p className="text-xs text-blue-600 mt-1">
                <Cloud className="w-3 h-3 inline mr-1" />
                Uploading to Cloudinary...
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Step Content */}
      <div className="bg-white rounded-xl border border-gray-200 p-8">
        {/* Step 1: Restaurant Search */}
        {step === 1 && (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-[#0A0C0B] mb-2">{t('create_menu_wizard.step_1.title')}</h2>
              <p className="text-[#687d76]">{t('create_menu_wizard.step_1.subtitle')}</p>
            </div>

           <div className="flex flex-col sm:flex-row gap-3">
  <div className="flex-1 relative">
    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-[#687d76]" />
    <input
      type="text"
      value={restaurantName}
      onChange={(e) => setRestaurantName(e.target.value)}
      onKeyPress={(e) => e.key === 'Enter' && searchRestaurant()}
      placeholder={t('create_menu_wizard.step_1.placeholder')}
      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#7BD5B5] focus:border-transparent"
    />
  </div>
  <button
    onClick={searchRestaurant}
    disabled={loading || !restaurantName.trim()}
    className="px-6 py-3 bg-[#0A0C0B] text-white rounded-lg font-medium hover:bg-[#1A1C1B] disabled:opacity-50 disabled:cursor-not-allowed w-full sm:w-auto"
  >
    {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : t('create_menu_wizard.step_1.search_button')}
  </button>
</div>


            {searchResults.length > 0 && (
              <div className="space-y-3">
                <h3 className="font-semibold text-[#0A0C0B]">{t('create_menu_wizard.step_1.select_prompt')}</h3>
                {searchResults.map((place) => (
                  <button
                    key={place.placeId}
                    onClick={() => handlePlaceSelect(place)}
                    className="w-full p-4 border border-gray-200 rounded-lg hover:border-[#7BD5B5] hover:bg-[#7BD5B5]/5 transition-all text-left"
                  >
                    <div className="flex items-start gap-4">
                      {place.logoUrl && (
                        <img src={place.logoUrl} alt={place.name} className="w-16 h-16 rounded-lg object-cover" />
                      )}
                      <div className="flex-1">
                        <h4 className="font-semibold text-[#0A0C0B]">{place.name}</h4>
                        <p className="text-sm text-[#687d76] mt-1 flex items-center gap-1">
                          <MapPin className="w-4 h-4" />
                          {place.address}
                        </p>
                      </div>
                      <ArrowRight className="w-5 h-5 text-[#687d76]" />
                    </div>
                  </button>
                ))}
              </div>
            )}

            <div className="pt-4 border-t border-gray-200">
              <p className="text-sm text-[#687d76] mb-2">{t('create_menu_wizard.step_1.manual_hint')}</p>
              <button
                type="button"
                onClick={handleAddManually}
                className="w-full sm:w-auto px-6 py-2.5 border-2 border-dashed border-[#7BD5B5] text-[#0A0C0B] rounded-lg font-medium hover:bg-[#7BD5B5]/10 transition-colors flex items-center justify-center gap-2"
              >
                <Building2 className="w-5 h-5" />
                {t('create_menu_wizard.step_1.add_manually')}
              </button>
            </div>
          </div>
        )}

        {/* Step 2: Review Details */}
        {step === 2 && restaurantDetails && (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-[#0A0C0B] mb-2">{t('create_menu_wizard.step_2.title')}</h2>
              <p className="text-[#687d76]">
                {isManualEntry ? t('create_menu_wizard.step_2.subtitle_manual') : t('create_menu_wizard.step_2.subtitle')}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {restaurantDetails.logoUrl && (
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-[#0A0C0B] mb-2">
                    {t('create_menu_wizard.step_2.logo_label')} <span className="text-xs text-gray-500">({t('create_menu_wizard.step_2.logo_subtitle')})</span>
                  </label>
                  <div className="flex items-center gap-4">
                    <img src={restaurantDetails.logoUrl} alt="Restaurant logo" className="w-32 h-32 rounded-lg object-cover border border-gray-200" />
                    <div className="text-sm text-gray-600">
                      <p className="flex items-center gap-1">
                        <Cloud className="w-4 h-4" />
                        <span>{t('create_menu_wizard.step_2.logo_auto_upload')}</span>
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-[#0A0C0B] mb-2">{t('create_menu_wizard.step_2.restaurant_name')}</label>
                <input
                  type="text"
                  value={restaurantDetails.name}
                  onChange={(e) => setRestaurantDetails({ ...restaurantDetails, name: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#7BD5B5] focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#0A0C0B] mb-2">{t('create_menu_wizard.step_2.address')}</label>
                <input
                  type="text"
                  value={restaurantDetails.address}
                  onChange={(e) => setRestaurantDetails({ ...restaurantDetails, address: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#7BD5B5] focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#0A0C0B] mb-2 flex items-center gap-2">
                  <Phone className="w-4 h-4" />
                  {t('create_menu_wizard.step_2.phone')}
                </label>
                <input
                  type="text"
                  value={restaurantDetails.phone || ''}
                  onChange={(e) => setRestaurantDetails({ ...restaurantDetails, phone: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#7BD5B5] focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#0A0C0B] mb-2 flex items-center gap-2">
                  <Globe className="w-4 h-4" />
                  {t('create_menu_wizard.step_2.website')}
                </label>
                <input
                  type="url"
                  value={restaurantDetails.website || ''}
                  onChange={(e) => setRestaurantDetails({ ...restaurantDetails, website: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#7BD5B5] focus:border-transparent"
                />
              </div>

              {restaurantDetails.colors && (
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-[#0A0C0B] mb-2">{t('create_menu_wizard.step_2.extracted_colors')}</label>
                  <div className="flex gap-3">
                    {restaurantDetails.colors.primary && (
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-12 h-12 rounded-lg border border-gray-200"
                          style={{ backgroundColor: restaurantDetails.colors.primary }}
                        />
                        <span className="text-sm text-[#687d76]">{t('create_menu_wizard.step_2.primary_color')}</span>
                      </div>
                    )}
                    {restaurantDetails.colors.secondary && (
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-12 h-12 rounded-lg border border-gray-200"
                          style={{ backgroundColor: restaurantDetails.colors.secondary }}
                        />
                        <span className="text-sm text-[#687d76]">{t('create_menu_wizard.step_2.secondary_color')}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-between pt-4">
              <button
                onClick={() => setStep(1)}
                className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-2"
              >
                <ArrowLeft className="w-4 h-4" />
                {t('create_menu_wizard.common.back')}
              </button>
              <button
                onClick={() => setStep(3)}
                className="px-6 py-2 bg-[#0A0C0B] text-white rounded-lg hover:bg-[#1A1C1B] flex items-center gap-2"
              >
                {t('create_menu_wizard.common.continue')}
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {/* Step 3: Theme Selection */}
        {step === 3 && (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-[#0A0C0B] mb-2">{t('create_menu_wizard.step_3.title')}</h2>
              <p className="text-[#687d76]">{t('create_menu_wizard.step_3.subtitle')}</p>
            </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
  <button
    onClick={() => setTheme('light')}
    className={`p-6 border-2 rounded-lg transition-all ${
      theme === 'light' 
        ? 'border-[#0A0C0B] bg-[#0A0C0B]/5' 
        : 'border-gray-200 hover:border-gray-300'
    }`}
  >
    <div className="w-full h-32 bg-white border border-gray-200 rounded mb-3"></div>
    <h3 className="font-semibold text-[#0A0C0B]">{t('create_menu_wizard.step_3.light_theme')}</h3>
    <p className="text-sm text-[#687d76] mt-1">{t('create_menu_wizard.step_3.light_theme_desc')}</p>
  </button>

  <button
    onClick={() => setTheme('dark')}
    className={`p-6 border-2 rounded-lg transition-all ${
      theme === 'dark' 
        ? 'border-[#0A0C0B] bg-[#0A0C0B]/5' 
        : 'border-gray-200 hover:border-gray-300'
    }`}
  >
    <div className="w-full h-32 bg-gray-900 rounded mb-3"></div>
    <h3 className="font-semibold text-[#0A0C0B]">{t('create_menu_wizard.step_3.dark_theme')}</h3>
    <p className="text-sm text-[#687d76] mt-1">{t('create_menu_wizard.step_3.dark_theme_desc')}</p>
  </button>
</div>


            <div>
              <label className="block text-sm font-medium text-[#0A0C0B] mb-2">{t('create_menu_wizard.step_3.primary_color')}</label>
              <div className="flex gap-3">
                <input
                  type="color"
                  value={primaryColor}
                  onChange={(e) => setPrimaryColor(e.target.value)}
                  className="w-16 h-10 border border-gray-300 rounded-lg cursor-pointer"
                />
                <input
                  type="text"
                  value={primaryColor}
                  onChange={(e) => setPrimaryColor(e.target.value)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#7BD5B5] focus:border-transparent"
                  placeholder="#3B82F6"
                />
              </div>
            </div>

            <div className="flex justify-between pt-4">
              <button
                onClick={() => setStep(2)}
                className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-2"
              >
                <ArrowLeft className="w-4 h-4" />
                {t('create_menu_wizard.common.back')}
              </button>
              <button
                onClick={() => setStep(4)}
                className="px-6 py-2 bg-[#0A0C0B] text-white rounded-lg hover:bg-[#1A1C1B] flex items-center gap-2"
              >
                {t('create_menu_wizard.common.continue')}
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {/* Step 4: Background Selection */}
        {step === 4 && (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-[#0A0C0B] mb-2">{t('create_menu_wizard.step_4.title')}</h2>
              <p className="text-[#687d76]">{t('create_menu_wizard.step_4.subtitle')}</p>
            </div>

            <div className="flex gap-4 mb-6">
              <button
                onClick={() => setBackgroundType('color')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  backgroundType === 'color'
                    ? 'bg-[#0A0C0B] text-white'
                    : 'bg-gray-100 text-[#0A0C0B] hover:bg-gray-200'
                }`}
              >
                {t('create_menu_wizard.step_4.solid_color')}
              </button>
              <button
                onClick={() => setBackgroundType('image')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  backgroundType === 'image'
                    ? 'bg-[#0A0C0B] text-white'
                    : 'bg-gray-100 text-[#0A0C0B] hover:bg-gray-200'
                }`}
              >
                {t('create_menu_wizard.step_4.background_image')}
              </button>
            </div>

            {backgroundType === 'color' ? (
              <div>
                <label className="block text-sm font-medium text-[#0A0C0B] mb-2">{t('create_menu_wizard.step_4.background_color')}</label>
                <div className="flex gap-3">
                  <input
                    type="color"
                    value={backgroundColor}
                    onChange={(e) => setBackgroundColor(e.target.value)}
                    className="w-16 h-10 border border-gray-300 rounded-lg cursor-pointer"
                  />
                  <input
                    type="text"
                    value={backgroundColor}
                    onChange={(e) => setBackgroundColor(e.target.value)}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#7BD5B5] focus:border-transparent"
                    placeholder="#ffffff"
                  />
                </div>
                <div 
                  className="mt-4 w-full h-32 rounded-lg border border-gray-200"
                  style={{ backgroundColor }}
                />
              </div>
            ) : (
              <div>
                <label className="block text-sm font-medium text-[#0A0C0B] mb-2">{t('create_menu_wizard.step_4.upload_background_image')}</label>
                
                {/* Image Preview */}
                {backgroundImagePreview && (
                  <div className="mb-4 relative">
                    <img 
                      src={backgroundImagePreview} 
                      alt="Background preview" 
                      className="w-full h-48 object-cover rounded-lg border border-gray-200"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        setBackgroundImagePreview('');
                        setBackgroundImageFile(null);
                        if (document.querySelector('#background-image-upload')) {
                          (document.querySelector('#background-image-upload') as HTMLInputElement).value = '';
                        }
                      }}
                      className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                )}
                
                {/* Upload Controls */}
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
                  <input
                    type="file"
                    id="background-image-upload"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        if (!file.type.startsWith('image/')) {
                          toast.error('Please select an image file');
                          return;
                        }
                        
                        if (file.size > 5 * 1024 * 1024) {
                          toast.error('Image size must be less than 5MB');
                          return;
                        }
                        
                        const previewUrl = URL.createObjectURL(file);
                        setBackgroundImagePreview(previewUrl);
                        setBackgroundImageFile(file);
                      }
                    }}
                    className="hidden"
                  />
                  
                  <label 
                    htmlFor="background-image-upload"
                    className="cursor-pointer flex flex-col items-center justify-center gap-3"
                  >
                    <div className="w-16 h-16 rounded-full bg-[#7BD5B5]/10 flex items-center justify-center">
                      <Upload className="w-8 h-8 text-[#7BD5B5]" />
                    </div>
                    
                    <div>
                      <p className="font-medium text-[#0A0C0B]">
                        {backgroundImageFile 
                          ? `${t('create_menu_wizard.step_4.selected_file', { filename: backgroundImageFile.name })}` 
                          : t('create_menu_wizard.step_4.click_to_upload')}
                      </p>
                      <p className="text-sm text-[#687d76] mt-1">
                        {t('create_menu_wizard.step_4.image_requirements')}
                      </p>
                    </div>
                    
                 
                  </label>
                </div>
                
                {/* Info message */}
                <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                  <div className="flex items-start gap-2">
                    <Cloud className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-blue-800">{t('create_menu_wizard.step_4.cloudinary_upload_title')}</p>
                      <p className="text-sm text-blue-700 mt-1">
                        {t('create_menu_wizard.step_4.cloudinary_upload_desc')}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="flex justify-between pt-4">
              <button
                onClick={() => setStep(3)}
                className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-2"
              >
                <ArrowLeft className="w-4 h-4" />
                {t('create_menu_wizard.common.back')}
              </button>
              <button
                onClick={() => setStep(5)}
                disabled={backgroundType === 'image' && !backgroundImageFile}
                className="px-6 py-2 bg-[#0A0C0B] text-white rounded-lg hover:bg-[#1A1C1B] disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {t('create_menu_wizard.common.continue')}
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {/* Step 5: Logo Upload */}
        {step === 5 && (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-[#0A0C0B] mb-2">{t('create_menu_wizard.step_5.title')}</h2>
              <p className="text-[#687d76]">{t('create_menu_wizard.step_5.subtitle')}</p>
            </div>

            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-[#0A0C0B] mb-2">Scegli il logo</label>
                
                {/* Option to use existing logo */}
                {restaurantDetails?.logoUrl && (
                  <div className="mb-6">
                    <div className="flex items-center gap-3 mb-3">
                      <input
                        type="radio"
                        id="use-existing-logo"
                        name="logo-choice"
                        checked={useExistingLogo}
                        onChange={() => {
                          setUseExistingLogo(true);
                          if (restaurantDetails?.logoUrl) {
                            setLogoPreview(restaurantDetails.logoUrl);
                          }
                          setLogoFile(null);
                          if (document.querySelector('#new-logo-upload')) {
                            (document.querySelector('#new-logo-upload') as HTMLInputElement).value = '';
                          }
                        }}
                        className="h-4 w-4 text-[#0A0C0B]"
                      />
                      <label htmlFor="use-existing-logo" className="text-sm font-medium text-[#0A0C0B]">
                       Usa il logo da Google Business
                      </label>
                    </div>
                    
                    <div className="ml-7 p-4 bg-gray-50 rounded-lg border border-gray-200">
                      <div className="flex items-center gap-4">
                        <img 
                          src={restaurantDetails.logoUrl} 
                          alt="Existing logo" 
                          className="w-24 h-24 rounded-lg object-contain border border-gray-200"
                        />
                        <div>
                          <p className="text-sm text-[#687d76] mb-1 flex items-center gap-1">
                            <Cloud className="w-4 h-4" />
                           Verrà caricato su Cloudinary
                          </p>
                          <p className="text-xs text-gray-500">Questo logo è stato recuperato automaticamente da Google e verrà caricato su Cloudinary.</p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Option to upload new logo */}
                <div className="mb-6">
                  <div className="flex items-center gap-3 mb-3">
                    <input
                      type="radio"
                      id="upload-new-logo"
                      name="logo-choice"
                      checked={!useExistingLogo}
                      onChange={() => {
                        setUseExistingLogo(false);
                        if (!logoPreview && !logoFile) {
                          setLogoPreview('');
                        }
                      }}
                      className="h-4 w-4 text-[#0A0C0B]"
                    />
                    <label htmlFor="upload-new-logo" className="text-sm font-medium text-[#0A0C0B]">
                      Carica un nuovo logo
                    </label>
                  </div>
                  
                  <div className="ml-7 p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="flex items-center gap-6">
                      <div className="flex flex-col items-center">
                        {logoPreview && !useExistingLogo ? (
                          <img 
                            src={logoPreview} 
                            alt="Logo preview" 
                            className="w-24 h-24 rounded-lg object-contain border border-gray-200"
                          />
                        ) : (
                          <div className="w-24 h-24 rounded-lg bg-gray-100 border-2 border-dashed border-gray-300 flex items-center justify-center">
                            <ImageIcon className="w-8 h-8 text-[#7BD5B5]/40" />
                          </div>
                        )}
                        <p className="text-xs text-gray-500 mt-2">Logo attuale</p>
                      </div>
                      
                      <div className="flex-1">
                        <label className="block mb-2 text-sm text-[#687d76]">Clicca per caricare un nuovo logo</label>
                        <input
                          type="file"
                          id="new-logo-upload"
                          accept="image/*"
                          onChange={(e) => {
                            if (e.target.files && e.target.files[0]) {
                              const file = e.target.files[0];
                              setLogoFile(file);
                              setLogoPreview(URL.createObjectURL(file));
                              setUseExistingLogo(false);
                            }
                          }}
                          className="w-full p-2 border border-gray-300 rounded-lg"
                        />
                        <p className="text-xs text-gray-500 mt-1">JPG, PNG, GIF fino a 10 MB</p>
                        
                        {logoFile && (
                          <button
                            type="button"
                            onClick={() => {
                              setLogoFile(null);
                              setLogoPreview('');
                              if (document.querySelector('#new-logo-upload')) {
                                (document.querySelector('#new-logo-upload') as HTMLInputElement).value = '';
                              }
                            }}
                            className="mt-2 px-3 py-1 text-sm bg-red-500 text-white rounded hover:bg-red-600"
                          >
                           Rimuovi logo
                          </button>
                        )}
                      </div>
                    </div>
                    
                    {/* Cloudinary info */}
                    {!useExistingLogo && logoFile && (
                      <div className="mt-4 p-3 bg-green-50 rounded-lg border border-green-200">
                        <div className="flex items-start gap-2">
                          <Cloud className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                          <div>
                            <p className="text-sm font-medium text-green-800">Pronto per il caricamento</p>
                            <p className="text-sm text-green-700 mt-1">
                             Questo logo verrà caricato quando creerai il menu.
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              
             
            </div>

         <div className="flex flex-col md:flex-row justify-between pt-4 gap-3">
  <button
    onClick={() => setStep(4)}
    className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-2"
  >
    <ArrowLeft className="w-4 h-4" />
    {t('create_menu_wizard.common.back')}
  </button>

  <button
    onClick={handleCreateMenu}
    disabled={loading || !restaurantDetails}
    className="px-6 py-2 bg-[#0A0C0B] text-white rounded-lg hover:bg-[#1A1C1B] disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
  >
    {loading ? (
      <>
        <Loader2 className="w-4 h-4 animate-spin" />
        {t('create_menu_wizard.step_6.creating')}
      </>
    ) : (
      <>
        <Cloud className="w-4 h-4" />
        {t('create_menu_wizard.step_6.create_with_cloudinary')}
      </>
    )}
  </button>
</div>

          </div>
        )}
      </div>
    </div>
  );
};

export default CreateMenuWizard;